// Core packages
import { Injectable } from "@angular/core";

// Third party packages
import { Observable } from "rxjs";
import { map } from "rxjs/operators";

// Custom packages
import { ApiResponse } from "src/app/shared/interfaces/_base/api-response.interface";
import {
  DataType,
  DataTypeCreatePayload,
  DataTypeSummary,
  DataTypeUpdatePayload,
} from "src/app/shared/interfaces/data-type.interface";
import { ApiService } from "src/app/shared/services/api.service";

/** Base path of the data types catalogue */
const BASE_PATH = "data-types";

/** The whole API is v2-only: force it regardless of the environment default */
const API_VERSION = "v2";

/**
 * Front-end API layer of the data types catalogue (/api/v2/data-types): the back-end
 * replacement of the hardcoded `dataType` dictionary.
 *
 * Every route, reads included, requires `canReadDocumentsList` AND `canCreateSettings`
 * on the CURRENTLY SELECTED role, so a caller without one of them gets a 403 and must
 * degrade instead of breaking.
 *
 * Until the back-end import has been run in an environment the list answers 200 with
 * an empty array: an empty catalogue is a legitimate state, not a failure.
 *
 * @since 3.0.0
 */
@Injectable({
  providedIn: "root",
})
export class DataTypesService {
  /**
   * Class constructor
   */
  constructor(private apiService: ApiService) {}

  /**
   * Catalogue WITHOUT the fields of the forms: `_id`, `dataTypeName`, `dataTypeLabel`
   * and `deleted` only.
   *
   * This is what a list or a picker needs, and `fields[]` is by far the heaviest part
   * of the payload: never load the full catalogue just to show its names.
   *
   * Ordered by `dataTypeName` ascending by the back-end.
   *
   * @since 3.0.0
   *
   * @param [includeDeleted] When true, hidden data types are returned too. Ask for
   *                         them when an existing reference has to be resolved: a
   *                         hidden data type stays referenced by the document types
   *                         that were pointing at it.
   */
  listSummaries(
    includeDeleted?: boolean
  ): Observable<ApiResponse<DataTypeSummary[]>> {
    return this.call("GET", BASE_PATH, {
      withFields: "false",
      includeDeleted: includeDeleted ? "true" : "false",
    });
  }

  /**
   * Full catalogue, fields included: what the rendering of a data entry form needs.
   *
   * @since 3.0.0
   *
   * @param [includeDeleted]    @see listSummaries
   * @param [withCommonFields] @see get
   */
  list(
    includeDeleted?: boolean,
    withCommonFields?: boolean
  ): Observable<ApiResponse<DataType[]>> {
    return this.call("GET", BASE_PATH, {
      withFields: "true",
      includeDeleted: includeDeleted ? "true" : "false",
      withCommonFields: withCommonFields ? "true" : "false",
    });
  }

  /**
   * A single data type, fields included.
   *
   * Answers hidden rows too, on purpose: `dataTypeId` is validated only on write, so a
   * data type hidden afterwards stays referenced and its form has to keep rendering.
   *
   * @since 3.0.0
   *
   * @param dataTypeId         The data type to read
   * @param [withCommonFields] Append the three fields every document form carries
   *                           (`contenutoDocumento`, `protocollo`, `keyword`) at the end of
   *                           `fields[]`.
   *
   *                           Ask for them to RENDER a document form: they are part of it, they
   *                           just live on the server instead of being copied into every row.
   *
   *                           Leave it off to EDIT the data type: without the flag the answer is
   *                           the stored row, which is the shape a PUT can send back. Sending the
   *                           common fields back would be rejected with a 400, since a data type
   *                           cannot declare those three names itself.
   */
  get(
    dataTypeId: string,
    withCommonFields?: boolean
  ): Observable<ApiResponse<DataType>> {
    return this.call("GET", `${BASE_PATH}/${dataTypeId}`, {
      withCommonFields: withCommonFields ? "true" : "false",
    });
  }

  /**
   * Create a data type. Answers 201 with the created row, 409 when `dataTypeName` is
   * already taken (hidden rows included).
   *
   * @since 3.0.0
   */
  create(
    payload: DataTypeCreatePayload
  ): Observable<ApiResponse<DataType>> {
    return this.call("POST", BASE_PATH, payload);
  }

  /**
   * Update a data type. Partial by design, EXCEPT `fields`: sending it replaces the
   * whole array. @see DataTypeUpdatePayload
   *
   * @since 3.0.0
   */
  update(
    dataTypeId: string,
    payload: DataTypeUpdatePayload
  ): Observable<ApiResponse<DataType>> {
    return this.call("PUT", `${BASE_PATH}/${dataTypeId}`, payload);
  }

  /**
   * HIDE a data type (`deleted: true`). Nothing is removed from the database and
   * GET /:id keeps serving it: the document types already bound to it must keep
   * rendering their form. Reversible with `restore`.
   *
   * Without `force` the back-end answers 409 when the data type is still used by
   * active document types, listing them in `message`. That is a question to forward to
   * the user, not an error to shout: ask, then call again with force.
   *
   * @since 3.0.0
   *
   * @param [force] Hide it even though document types still reference it
   */
  hide(dataTypeId: string, force?: boolean): Observable<ApiResponse<DataType>> {
    return this.call(
      "DELETE",
      `${BASE_PATH}/${dataTypeId}?force=${force ? "true" : "false"}`
    );
  }

  /**
   * Bring a hidden data type back (`deleted: false`).
   *
   * @since 3.0.0
   */
  restore(dataTypeId: string): Observable<ApiResponse<DataType>> {
    return this.call("PATCH", `${BASE_PATH}/${dataTypeId}/restore`, {});
  }

  /**
   * Thin wrapper around ApiService pinning the v2 version on every call.
   *
   * On a network failure (status 0) ApiService emits an Error as a NEXT value instead
   * of erroring: left as is, the caller would treat it as an empty successful response
   * and silently show an empty catalogue. It is turned back into a real error here.
   *
   * @since 3.0.0
   */
  private call(method: string, path: string, params?: any): Observable<any> {
    return this.apiService
      .apiCall(
        method,
        path,
        params,
        undefined,
        undefined,
        undefined,
        API_VERSION
      )
      .pipe(
        map((res: any) => {
          if (res instanceof Error) {
            throw res;
          }
          return res;
        })
      );
  }
}
